import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WorkflowFileInput } from '@/hooks/queries/triggers/use-workflow-file-inputs';

// The field type the run path uses for a delivered file. Must match the
// backend's _FILE_FIELD_TYPE or the binding is rejected at create.
const FILE_FIELD_TYPE = 'FileUploadResponse';

export const optionKey = (input: WorkflowFileInput) =>
  `${input.node_instance_id}:${input.field_id}`;

/**
 * Build the explicit binding for a chosen node, or omit it entirely.
 *
 * Returning undefined lets the backend auto-discover the single file input,
 * which keeps the common one-node case free of a pointless choice.
 */
export const buildInputBinding = (input?: WorkflowFileInput) =>
  input
    ? [
        {
          node_instance_id: input.node_instance_id,
          fields: [
            {
              field_id: input.field_id,
              type: FILE_FIELD_TYPE,
              required: true,
            },
          ],
        },
      ]
    : undefined;

const baseLabel = (input: WorkflowFileInput) =>
  `${input.node_display_name} — ${input.display_name}`;

/**
 * Option labels, numbered only where they would otherwise be identical.
 *
 * `node_display_name` is the node's authored name when it has one, so renaming
 * on the canvas already disambiguates. Most people never rename, which leaves
 * two options reading "File Input — Upload Files"; numbering them in graph order
 * matches the order they appear in the editor's "Nodes in Workflow" list.
 */
export const optionLabels = (
  options: WorkflowFileInput[],
): Map<string, string> => {
  const totals = new Map<string, number>();
  options.forEach((i) =>
    totals.set(baseLabel(i), (totals.get(baseLabel(i)) ?? 0) + 1),
  );

  const seen = new Map<string, number>();
  const labels = new Map<string, string>();
  options.forEach((i) => {
    const base = baseLabel(i);
    if ((totals.get(base) ?? 0) < 2) {
      labels.set(optionKey(i), base);
      return;
    }
    const nth = (seen.get(base) ?? 0) + 1;
    seen.set(base, nth);
    labels.set(optionKey(i), `${base} #${nth}`);
  });
  return labels;
};

interface FileInputSelectProps {
  options: WorkflowFileInput[];
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
}

/** Which node receives the file, when a workflow has more than one that can. */
export const FileInputSelect = ({
  options,
  value,
  onChange,
  disabled,
}: FileInputSelectProps) => {
  const labels = optionLabels(options);
  const numbered = [...labels.values()].some((l) => l.includes(' #'));

  return (
    <>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder='Which node receives the file?' />
        </SelectTrigger>
        <SelectContent>
          {options.map((input) => (
            <SelectItem key={optionKey(input)} value={optionKey(input)}>
              {labels.get(optionKey(input))}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {numbered && (
        <p className='text-xs text-muted-foreground'>
          Nodes sharing a name are numbered in the order they appear in Nodes in
          Workflow. Rename them on the canvas to tell them apart.
        </p>
      )}
    </>
  );
};
